/**
 * Group-level Force helpers (PMDEV-3736). Group Force must act on exactly the rows shown under
 * the group, so it is blocked whenever the by-filter criteria would select a different set.
 */

/** Group columns the byFilter endpoint understands. Any other group key would be rejected. */
export const GROUP_FORCE_FIELDS = [
  "AppName",
  "Type",
  "ValuationDate",
  "Client",
  "ClientAgent",
  "SchemeId",
  "Scheme",
  "Location",
  "DependencyType",
] as const;

export const GROUP_FORCE_BLOCKED_REASON =
  `Group force is only available when grouping by ${GROUP_FORCE_FIELDS.slice(0, -1).join(", ")} or ${GROUP_FORCE_FIELDS[GROUP_FORCE_FIELDS.length - 1]}, and text filters use 'equals'.`;

export const BLANK_GROUP_REASON =
  "Group force is not available for a blank group; select the rows instead.";

// Grid filters getCurrentFilterCriteria maps to byFilter criteria, and how each must be set so
// the server (exact match) selects the same rows as the grid.
const TEXT_FILTER_FIELDS = ["AppName", "Type", "Client", "ClientAgent", "Scheme", "Location", "DependencyType"];
const EXACT_MATCH_FILTER_FIELDS = ["status", "dependencyName"];

type ColumnFilterModel = { type?: string; filterType?: string } | null | undefined;

/**
 * Lower-cases the first letter of each group key so it matches CheckByFilterRequest
 * (e.g. { AppName, ValuationDate } becomes { appName, valuationDate }).
 */
export function normaliseGroupKeys(groupKeys: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(groupKeys).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v])
  );
}

/**
 * Why group Force is unavailable for this group under the current grid filters, or undefined
 * when it is available.
 */
export function getGroupForceBlockedReason(
  groupKeys: Record<string, string>,
  filterModel: Record<string, ColumnFilterModel>,
  hasBlankGroupKey = false
): string | undefined {
  const allowed: readonly string[] = GROUP_FORCE_FIELDS;

  if (Object.keys(groupKeys).some((field) => !allowed.includes(field))) {
    return GROUP_FORCE_BLOCKED_REASON;
  }

  // A blank key is dropped from the request (or rejected by the API), so the force would cover
  // the whole parent group.
  if (hasBlankGroupKey || Object.values(groupKeys).some((value) => value.trim() === "")) {
    return BLANK_GROUP_REASON;
  }

  for (const [field, model] of Object.entries(filterModel ?? {})) {
    if (!model) {
      continue;
    }

    if (TEXT_FILTER_FIELDS.includes(field) || field === "SchemeId") {
      if (model.type !== "equals") {
        return GROUP_FORCE_BLOCKED_REASON;
      }
    } else if (field === "ValuationDate") {
      if (model.type !== "equals" && model.type !== "inRange") {
        return GROUP_FORCE_BLOCKED_REASON;
      }
    } else if (!EXACT_MATCH_FILTER_FIELDS.includes(field)) {
      // Not sent to byFilter at all, so the force would include rows this filter hides.
      return `Group force is not available while the ${field} column is filtered; remove that filter or select the rows instead.`;
    }
  }

  return undefined;
}

/**
 * True when the node or any ancestor group has a null / blank key. getGroupKeysFromNode leaves
 * null keys out, so this has to be read from the node chain itself.
 */
export function hasBlankGroupKey(node: { field?: string | null; key?: unknown; group?: boolean; parent?: unknown } | null): boolean {
  let current = node as { field?: string | null; key?: unknown; group?: boolean; parent?: unknown } | null;
  while (current) {
    if (current.group && current.field && (current.key == null || String(current.key).trim() === "")) {
      return true;
    }
    current = (current.parent ?? null) as typeof current;
  }
  return false;
}

/** Ordered "Field=value" crumbs from the outermost group to the clicked one. */
export function toGroupPath(groupKeys: Record<string, string>): string[] {
  // getGroupKeysFromNode walks from the clicked node up to the root, so reverse it.
  return Object.entries(groupKeys)
    .reverse()
    .map(([field, value]) => `${field}=${field === "ValuationDate" ? value.split("T")[0] : value}`);
}
